import React, { useState, useRef, useEffect } from 'react';
import { Accordion } from 'react-bootstrap';
import ConditionComponent from './ConditionComponent';
import TargetComponent from './TargetComponent';
import IncentiveComponent from './IncentiveComponent';
import PromotionSettings from "./PromotionSettings";
import StrategyComponent from "./StrategyComponent";
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MdEdit, MdInfoOutline, MdGroup, MdList, MdAttachMoney } from 'react-icons/md';
import EditPromotionOverlay from "../../../models/global/DataModels/Campaign/Promotion/EditPromotionOverlay";
import Incentive from "../../../models/global/DataModels/Campaign/Promotion/Incentive";
import { Strategy } from "../../../models/global/DataModels/Campaign/Promotion/Strategy";
import Condition from "../../../models/global/DataModels/Campaign/Promotion/Condition";
import Target from "../../../models/global/DataModels/Campaign/Promotion/Target";
import PromotionOverlay from "../../../models/global/DataModels/Campaign/Promotion/PromotionOverlay";
import MainButton from '../../../shared/layout/buttons/MainButton';
import { StyledAccordion } from '../../../shared/layout/display/StyledAccordion';

const PromotionEdit = ({
    campaignRef,
    selectedPromotionIndex,
    onUpdatePromotion
}) => {
    const { t } = useTranslation();
    const [isOverlayModalOpen, setOverlayModalOpen] = useState(false);
    const [, forceUpdate] = useState(0);
    const previewContainerRef = useRef(null);
    const [previewScale, setPreviewScale] = useState(1);

    useEffect(() => {
        const updateScale = () => {
            if (previewContainerRef.current) {
                const width = previewContainerRef.current.offsetWidth;
                // Assuming base design width is 1920px (standard HD)
                // Adjust this base width if your design target is different (e.g., 1280px)
                const baseWidth = 1920;
                setPreviewScale(width / baseWidth);
            }
        };

        updateScale();
        window.addEventListener('resize', updateScale);
        return () => window.removeEventListener('resize', updateScale);
    }, [previewContainerRef.current]); // Re-run if ref changes (though ref object is stable)


    const handleInputChange = (field, value) => {
        if (!campaignRef.current) return;

        const selectedPromotion = campaignRef.current.promotions[selectedPromotionIndex];

        if (field.startsWith("conditions")) {
            const [_, indexStr, subField] = field.split(".");
            const index = parseInt(indexStr, 10);
            const condition = new Condition(selectedPromotion.conditions[index]);
            condition[subField] = value;
            selectedPromotion.conditions[index] = condition;
        } else if (field.startsWith("targets")) {
            const [_, indexStr, subField] = field.split(".");
            const index = parseInt(indexStr, 10);
            const target = new Target(selectedPromotion.targets[index]);
            target[subField] = value;
            selectedPromotion.targets[index] = target;
        } else if (field.startsWith("incentives")) {
            const [_, indexStr, subField] = field.split(".");
            const index = parseInt(indexStr, 10);
            const incentive = new Incentive(selectedPromotion.incentives[index]);
            incentive[subField] = value;
            selectedPromotion.incentives[index] = incentive;
        } else if (field.startsWith("strategies")) {
            const [_, indexStr, subField] = field.split(".");
            const index = parseInt(indexStr, 10);
            const strategy = new Strategy(selectedPromotion.strategies[index]);
            strategy[subField] = value;
            selectedPromotion.strategies[index] = strategy;
        } else if (field === "promotionOverlay") {
            selectedPromotion.promotionOverlay = new PromotionOverlay(value);
        } else {
            selectedPromotion[field] = value;
        }
    };

    const handleAddTarget = () => {
        if (campaignRef.current) {
            campaignRef.current.promotions[selectedPromotionIndex].targets.push(new Target());
            forceUpdate((prev) => prev + 1);
        }
    };

    const handleAddIncentive = () => {
        if (campaignRef.current) {
            campaignRef.current.promotions[selectedPromotionIndex].incentives.push(new Incentive());
            forceUpdate((prev) => prev + 1);
        }
    };

    const handleAddStrategy = () => {
        if (campaignRef.current) {
            campaignRef.current.promotions[selectedPromotionIndex].strategies.push(new Strategy());
            forceUpdate((prev) => prev + 1);
        }
    };
    const handleAddCondition = () => {
        if (!campaignRef.current) return;
        const selectedPromotion = campaignRef.current.promotions[selectedPromotionIndex];
        if (!selectedPromotion.conditions) {
            selectedPromotion.conditions = [];
        }
        selectedPromotion.conditions.push(new Condition());
        forceUpdate((prev) => prev + 1);
    };


    const handleDeleteTarget = (index) => {
        if (campaignRef.current) {
            campaignRef.current.promotions[selectedPromotionIndex].targets.splice(index, 1);
            forceUpdate((prev) => prev + 1);
        }
    };

    const handleDeleteStrategy = (index) => {
        if (campaignRef.current) {
            campaignRef.current.promotions[selectedPromotionIndex].strategies.splice(index, 1);
            forceUpdate((prev) => prev + 1);
        }
    };

    const handleDeleteCondition = (index) => {
        if (campaignRef.current) {
            campaignRef.current.promotions[selectedPromotionIndex].conditions.splice(index, 1);
            forceUpdate((prev) => prev + 1);
        }
    };

    const handleDeleteIncentive = (index) => {
        if (campaignRef.current) {
            campaignRef.current.promotions[selectedPromotionIndex].incentives.splice(index, 1);
            forceUpdate((prev) => prev + 1);
        }
    };

    const handleEditOverlay = () => setOverlayModalOpen(true);
    const handleOverlaySave = (updatedOverlay) => {
        const selectedPromotion = campaignRef.current.promotions[selectedPromotionIndex];
        selectedPromotion.promotionOverlay = new PromotionOverlay(updatedOverlay);
        forceUpdate((prev) => prev + 1);
        setOverlayModalOpen(false);
    };
    const handleOverlayCancel = () => setOverlayModalOpen(false);

    return (
        <Container>
            <Header>
                <MdEdit style={{ marginRight: '8px', color: 'rgba(130, 22, 146, 1)' }} />
                {t('Campaigns.PromotionEdit.editPromotion', {
                    promotionName: campaignRef.current.promotions[selectedPromotionIndex]?.name || t('Campaigns.PromotionEdit.promotionName'),
                })}
            </Header>

            <EditGrid>
                <LeftColumn>
                    <StyledAccordion defaultActiveKey="0" flush>
                        {/* Basic Information Section */}
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <MdInfoOutline style={{ marginRight: '8px', color: 'rgba(130, 22, 146, 1)' }} size={20} />
                                {t('Campaigns.PromotionEdit.basicInformation')}
                            </Accordion.Header>
                            <Accordion.Body>
                                <PromotionSettings
                                    promotion={campaignRef.current.promotions[selectedPromotionIndex]}
                                />
                            </Accordion.Body>
                        </Accordion.Item>
                        {/* Targets Section */}
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>
                                <MdGroup style={{ marginRight: '8px', color: 'rgba(130, 22, 146, 1)' }} size={20} />
                                {t('Campaigns.PromotionEdit.targets', { count: campaignRef.current.promotions[selectedPromotionIndex]?.targets?.length || 0 })}
                            </Accordion.Header>
                            <Accordion.Body>
                                <ItemsContainer>
                                    {campaignRef.current.promotions[selectedPromotionIndex]?.targets?.map((target, index) => (
                                        <TargetComponent
                                            key={`target_${index}`}
                                            index={index}
                                            target={target}
                                            onChange={(field, value) => handleInputChange(`targets.${index}.${field}`, value)}
                                            onDelete={() => handleDeleteTarget(index)}
                                        />
                                    ))}
                                </ItemsContainer>
                                <MainButton size="small" variant="secondary" onClick={handleAddTarget}>
                                    {t('Campaigns.PromotionEdit.addTarget')}
                                </MainButton>
                            </Accordion.Body>
                        </Accordion.Item>
                        {/* Strategies Section */}
                        <Accordion.Item eventKey="2">
                            <Accordion.Header>
                                <MdList style={{ marginRight: '8px', color: 'rgba(130, 22, 146, 1)' }} size={20} />
                                {t('Campaigns.PromotionEdit.strategies', { count: campaignRef.current.promotions[selectedPromotionIndex]?.strategies?.length || 0 })}
                            </Accordion.Header>
                            <Accordion.Body>
                                <ItemsContainer>
                                    {campaignRef.current.promotions[selectedPromotionIndex]?.strategies?.map((strategy, index) => (
                                        <StrategyComponent
                                            key={`strategy_${index}`}
                                            index={index}
                                            strategy={strategy}
                                            onChange={(field, value) => handleInputChange(`strategies.${index}.${field}`, value)}
                                            onDelete={() => handleDeleteStrategy(index)}
                                        />
                                    ))}
                                </ItemsContainer>
                                <MainButton size="small" variant="secondary" onClick={handleAddStrategy}>
                                    {t('Campaigns.PromotionEdit.addStrategy')}
                                </MainButton>
                            </Accordion.Body>
                        </Accordion.Item>
                        {/* Conditions Section */}
                        <Accordion.Item eventKey="3">
                            <Accordion.Header>
                                <MdList style={{ marginRight: '8px', color: 'rgba(130, 22, 146, 1)' }} size={20} />
                                {t('Campaigns.PromotionEdit.conditions', { count: campaignRef.current.promotions[selectedPromotionIndex]?.conditions?.length || 0 })}
                            </Accordion.Header>
                            <Accordion.Body>
                                <ItemsContainer>
                                    {campaignRef.current.promotions[selectedPromotionIndex]?.conditions?.map((condition, index) => (
                                        <ConditionComponent
                                            key={`condition_${index}`}
                                            index={index}
                                            condition={condition}
                                            onChange={(field, value) => handleInputChange(`conditions.${index}.${field}`, value)}
                                            onDelete={() => handleDeleteCondition(index)}
                                        />
                                    ))}
                                </ItemsContainer>
                                <MainButton size="small" variant="secondary" onClick={handleAddCondition}>
                                    {t('Campaigns.PromotionEdit.addCondition')}
                                </MainButton>
                            </Accordion.Body>
                        </Accordion.Item>
                        {/* Incentives Section */}
                        <Accordion.Item eventKey="4">
                            <Accordion.Header>
                                <MdAttachMoney style={{ marginRight: '8px', color: 'rgba(130, 22, 146, 1)' }} size={20} />
                                {t('Campaigns.PromotionEdit.incentives', { count: campaignRef.current.promotions[selectedPromotionIndex]?.incentives?.length || 0 })}
                            </Accordion.Header>
                            <Accordion.Body>
                                <ItemsContainer>
                                    {campaignRef.current.promotions[selectedPromotionIndex]?.incentives?.map((incentive, index) => (
                                        <IncentiveComponent
                                            key={`incentive_${index}`}
                                            index={index}
                                            incentive={incentive}
                                            onChange={(field, value) => handleInputChange(`incentives.${index}.${field}`, value)}
                                            onDelete={() => handleDeleteIncentive(index)}
                                        />
                                    ))}
                                </ItemsContainer>
                                <MainButton size="small" variant="secondary" onClick={handleAddIncentive}>
                                    {t('Campaigns.PromotionEdit.addIncentive')}
                                </MainButton>
                            </Accordion.Body>
                        </Accordion.Item>
                    </StyledAccordion>
                </LeftColumn>
                <RightColumn>
                    <StickyOverlay>
                        <PreviewTitle>
                            <MdInfoOutline size={20} />
                            {t('Campaigns.PromotionEdit.promotionOverlay')}
                        </PreviewTitle>
                        <DeviceFrame>
                            <FrameHeader>
                                <span className="dot" />
                                <span className="dot" />
                                <span className="dot" />
                            </FrameHeader>
                            <PreviewContainer ref={previewContainerRef}>
                                {campaignRef.current.promotions[selectedPromotionIndex]?.promotionOverlay.render(
                                    campaignRef.current.promotions[selectedPromotionIndex],
                                    previewScale
                                )}
                            </PreviewContainer>
                        </DeviceFrame>
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <MainButton size="small" variant="secondary" onClick={handleEditOverlay} icon={<MdEdit />}>
                                {t('Campaigns.PromotionEdit.editOverlay')}
                            </MainButton>
                        </div>
                    </StickyOverlay>
                </RightColumn>
            </EditGrid>

            {isOverlayModalOpen && (
                <EditPromotionOverlay
                    promotion={campaignRef.current.promotions[selectedPromotionIndex]}
                    onSave={handleOverlaySave}
                    onCancel={handleOverlayCancel}
                />
            )}
        </Container>
    );
};

export default PromotionEdit;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const Header = styled.h4`
    display: flex;
    align-items: center;
    font-size: 1.25rem;
    color: #111827;
    margin: 0;
    font-weight: 600;
`;

const EditGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;

    @media (min-width: 1024px) {
        grid-template-columns: 3fr 2fr;
    }
`;

const LeftColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const RightColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const StickyOverlay = styled.div`
    position: sticky;
    top: 24px;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const PreviewTitle = styled.h5`
    font-size: 0.95rem;
    font-weight: 700;
    color: #821692;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const DeviceFrame = styled.div`
    background: #ffffff;
    border-radius: 20px;
    border: 8px solid #111827;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    position: relative;
`;

const FrameHeader = styled.div`
    background: #111827;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    padding: 0 1rem;

    .dot {
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: #374151;
    }
`;

const PreviewContainer = styled.div`
    background: #f3f4f6;
    aspect-ratio: 16/9;
    position: relative;
    overflow: hidden;
`;

const ItemsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.25rem;
`;
